import type { WorkScheduleCalendarAccess } from "../../interfaces/interfaces";

type CalendarEmployeeId = number | null | undefined;

type BuildWorkScheduleCalendarAccessParams = {
    currentBranchEmployeeId: number | null;
    effectiveEmployeeId: number | null;
    isSingleEmployeeMode: boolean;
    canOpenEmployeeLinkAll: boolean;
    canOpenEmployeeLinkSelf: boolean;
    canCreateVacationAll: boolean;
    canCreateVacationSelf: boolean;
    canCreateCustomDayAll: boolean;
    canCreateCustomDaySelf: boolean;
    canEditCustomDayAll: boolean;
    canEditCustomDaySelf: boolean;
    canCreateScheduleAll: boolean;
    canCreateScheduleSelf: boolean;
    canEditScheduleAll: boolean;
    canEditScheduleSelf: boolean;
    canDeleteScheduleAll: boolean;
    canDeleteScheduleSelf: boolean;
};

export const isOwnCalendarEmployee = (
    employeeId: CalendarEmployeeId,
    currentBranchEmployeeId: number | null
): boolean => Boolean(currentBranchEmployeeId && employeeId && currentBranchEmployeeId === employeeId);

export const resolveCalendarEmployeeAccess = (
    employeeId: CalendarEmployeeId,
    currentBranchEmployeeId: number | null,
    canAccessAll: boolean,
    canAccessSelf: boolean
): boolean => {
    if (!employeeId) {
        return false;
    }

    return isOwnCalendarEmployee(employeeId, currentBranchEmployeeId) ? canAccessAll || canAccessSelf : canAccessAll;
};

export const buildWorkScheduleCalendarAccess = ({
    currentBranchEmployeeId,
    effectiveEmployeeId,
    isSingleEmployeeMode,
    canOpenEmployeeLinkAll,
    canOpenEmployeeLinkSelf,
    canCreateVacationAll,
    canCreateVacationSelf,
    canCreateCustomDayAll,
    canCreateCustomDaySelf,
    canEditCustomDayAll,
    canEditCustomDaySelf,
    canCreateScheduleAll,
    canCreateScheduleSelf,
    canEditScheduleAll,
    canEditScheduleSelf,
    canDeleteScheduleAll,
    canDeleteScheduleSelf,
}: BuildWorkScheduleCalendarAccessParams): WorkScheduleCalendarAccess => ({
    context: {
        currentBranchEmployeeId,
        effectiveEmployeeId,
        isSingleEmployeeMode,
    },
    employeeLink: {
        canOpenForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(
                employeeId,
                currentBranchEmployeeId,
                canOpenEmployeeLinkAll,
                canOpenEmployeeLinkSelf
            ),
    },
    vacation: {
        canCreateForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(
                employeeId,
                currentBranchEmployeeId,
                canCreateVacationAll,
                canCreateVacationSelf
            ),
    },
    customDay: {
        canCreateForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(
                employeeId,
                currentBranchEmployeeId,
                canCreateCustomDayAll,
                canCreateCustomDaySelf
            ),
        canEditForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(
                employeeId,
                currentBranchEmployeeId,
                canEditCustomDayAll,
                canEditCustomDaySelf
            ),
    },
    schedule: {
        canCreateForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(
                employeeId,
                currentBranchEmployeeId,
                canCreateScheduleAll,
                canCreateScheduleSelf
            ),
        canEditForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(employeeId, currentBranchEmployeeId, canEditScheduleAll, canEditScheduleSelf),
        canDeleteForEmployee: (employeeId) =>
            resolveCalendarEmployeeAccess(
                employeeId,
                currentBranchEmployeeId,
                canDeleteScheduleAll,
                canDeleteScheduleSelf
            ),
    },
});
